import { createApi } from '@reduxjs/toolkit/query/react';

import { VIOLATIONS_TAB_LIST_AD_FILTER } from '../../../shared/constants';
import { axiosBaseQuery } from '../../../shared/services/baseQuery';

import {
  type FiltersGeneralProps,
  type GetAccountsApiResponse,
  type GetApplicationEndpointApiResponse,
  type GetBusinessProcessApiResponse,
  type GetColumnPickerApiResponse,
  type GetEntitlementsApiResponse,
  type GetEntitlementTypessApiResponse,
  type GetFunctionsApiResponse,
  type GetInsightDetailsApiResponse,
  type GetMitigatingControlsApiResponse,
  type GetRiskApiResponse,
  type GetRulesetsApiResponse,
  type GetTabCountApiResponse,
  type GetUserFiltersApiResponse,
  type GetViolationsGraphClusterResponse,
  type GetViolationsGraphResponse,
  type SodViolationsListProps,
  ViolationsTabType,
} from './violationsTabTypes';

export const violationsTabApi = createApi({
  reducerPath: ViolationsTabType.API_SLICE_NAME,
  baseQuery: axiosBaseQuery({}),
  endpoints: () => ({}),
});

export const violationsTabApiEndpoints = violationsTabApi.injectEndpoints({
  endpoints: (builder) => ({
    getTabCount: builder.query<unknown, void>({
      query: () => ({
        url: '/api/sodms/sod-violations/status/counts',
        isECMv6: true,
      }),
      transformResponse: (response: { data: GetTabCountApiResponse }) =>
        response.data,
    }),
    getColumnPicker: builder.query<unknown, string>({
      query: (view) => ({
        url: '/api/sodms/view/columns',
        params: {
          view,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetColumnPickerApiResponse) => {
        if (response.status === 200) {
          return response.data;
        }

        return [];
      },
    }),
    getRisks: builder.mutation<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/sodms/risks',
        method: 'POST',
        data: {
          includeDetails: true,
          riskName: `${payload.search ?? ''}`,
          max: payload.max,
          offset: payload.offset,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetRiskApiResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          const uniqueValues = [
            ...new Set(response.data?.map((risk) => risk.riskName) ?? []),
          ];

          return uniqueValues
            .filter(Boolean)
            .filter((value) => value.trim())
            .map((value) => ({ label: value, value, isChecked: false }));
        }

        return [];
      },
    }),
    getBusinessProcess: builder.query<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/sodms/business-process',
        params: {
          includeDetails: 'true',
          max: payload.max,
          offset: payload.offset,
          businessProcess: `${payload.search ?? ''}`,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetBusinessProcessApiResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          const uniqueValuesList: string[] = [];

          response.data?.forEach((bp) => {
            const value = bp.businessProcess;

            if (!uniqueValuesList.includes(value)) {
              uniqueValuesList.push(value);
            }
          });

          return uniqueValuesList
            .filter((uniqueValue) => uniqueValue.trim().length > 0)
            .map((uniqueValue) => ({
              label: `${uniqueValue}`,
              value: `${uniqueValue}`,
              isChecked: false,
            }));
        }

        return [];
      },
    }),
    getFunctions: builder.query<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/sodms/functions',
        params: {
          includeDetails: 'true',
          max: payload.max,
          offset: payload.offset,
          functionName: `${payload.search ?? ''}`,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetFunctionsApiResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          const uniqueValues = [
            ...new Set(response.data?.map((func) => func.functionName) ?? []),
          ];

          return uniqueValues
            .filter(Boolean)
            .filter((value) => value.trim())
            .map((value) => ({ label: value, value, isChecked: false }));
        }

        return [];
      },
    }),
    getRulesets: builder.query<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/sodms/rulesets',
        params: {
          includeDetails: 'true',
          max: payload.max,
          offset: payload.offset,
          rulesetName: `${payload.search ?? ''}`,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetRulesetsApiResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          return response.data?.map((ruleset) => ({
            label: `${ruleset.rulesetName}`,
            value: `${ruleset.id}`,
            isChecked: false,
          }));
        }

        return [];
      },
    }),
    getApplicationEndpoints: builder.mutation<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/v5/getEndpoints',
        method: 'POST',
        params: {
          max: payload.max,
          offset: payload.offset,
          endpointname:
            `${payload.search ?? ''}`.trim().length > 0
              ? `${payload.search ?? ''}`
              : undefined,
        },
      }),
      transformResponse: (response: GetApplicationEndpointApiResponse) => {
        if (
          response.errorCode === '0' &&
          response.displayCount &&
          response.displayCount > 0
        ) {
          const uniqueValuesList: string[] = [];

          response.endpoints?.forEach((endpoint) => {
            const value = endpoint.displayName;

            if (!uniqueValuesList.includes(value)) {
              uniqueValuesList.push(value);
            }
          });

          return uniqueValuesList
            .filter((uniqueValue) => uniqueValue.trim().length > 0)
            .map((uniqueValue) => ({
              label: `${uniqueValue}`,
              value: `${uniqueValue}`,
              isChecked: false,
            }));
        }

        return [];
      },
    }),
    getUsers: builder.mutation<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/v5/getUser',
        method: 'POST',
        data: {
          filtercriteria: {
            [payload.key ?? 'username']: `*${payload.search ?? ''}*`,
          },
          showsecurityanswers: '1',
        },
        params: {
          max: payload.max,
          offset: payload.offset,
        },
      }),
      transformResponse: (response: GetUserFiltersApiResponse, _, payload) => {
        if (
          response.errorCode === '0' &&
          response.displaycount &&
          response.displaycount > 0
        ) {
          type UserListItem = NonNullable<
            GetUserFiltersApiResponse['userlist']
          >[number];

          const isForUniqueValues = Boolean(
            payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.COSTCENTER.value ||
              payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.FIRSTNAME.value ||
              payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.LASTNAME.value ||
              payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.USERNAME.value
          );

          if (isForUniqueValues) {
            const uniqueValuesList: string[] = [];

            response.userlist?.forEach((user) => {
              const value =
                user[(payload.key ?? 'username') as keyof UserListItem];

              if (!uniqueValuesList.includes(value)) {
                uniqueValuesList.push(value);
              }
            });

            return uniqueValuesList
              .filter((uniqueValue) => uniqueValue.trim().length > 0)
              .map((uniqueValue) => ({
                label: `${uniqueValue}`,
                value: `${uniqueValue}`,
                isChecked: false,
              }));
          }

          return response.userlist?.map((user) => ({
            label: user[(payload.key ?? 'username') as keyof UserListItem],
            value: `${user.userKey}`,
            isChecked: false,
          }));
        }

        return [];
      },
    }),
    getAccounts: builder.mutation<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/v5/getAccounts',
        method: 'POST',
        params: { max: payload.max, offset: payload.offset },
        data: {
          accountQuery: "acc.status='1'",
          advsearchcriteria: {
            status: 'ACTIVE',
            [payload.key ?? 'name']: `*${payload.search ?? ''}*`,
          },
        },
      }),
      transformResponse: (response: GetAccountsApiResponse, _, payload) => {
        if (
          response.errorCode === '0' &&
          response.displaycount &&
          response.displaycount > 0
        ) {
          type AccountListItem = NonNullable<
            GetAccountsApiResponse['Accountdetails']
          >[number];

          return response.Accountdetails?.map((account) => ({
            label: account[(payload.key ?? 'name') as keyof AccountListItem],
            value: `${account.accountKey}`,
            isChecked: false,
          }));
        }

        return [];
      },
    }),
    getEntitlements: builder.mutation<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/v5/getEntitlements',
        method: 'POST',
        params: { max: payload.max, offset: payload.offset },
        data: {
          entQuery: `ent.entitlement_value like '%${payload.search ?? ''}%'`,
        },
      }),
      transformResponse: (response: GetEntitlementsApiResponse) => {
        if (
          response.errorCode === '0' &&
          response.entitlementsCount &&
          response.entitlementsCount > 0
        ) {
          const uniqueValuesList: string[] = [];

          response.Entitlementdetails?.forEach((entitlement) => {
            const value = entitlement.entitlement_value;

            if (!uniqueValuesList.includes(value)) {
              uniqueValuesList.push(value);
            }
          });

          return uniqueValuesList
            .filter((uniqueValue) => uniqueValue.trim().length > 0)
            .map((uniqueValue) => ({
              label: `${uniqueValue}`,
              value: `${uniqueValue}`,
              isChecked: false,
            }));
        }

        return [];
      },
    }),
    getEntitlementTypes: builder.query<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/v5/getEntitlementTypes',
        params: {
          max: payload.max,
          offset: payload.offset,
          entitlementname: `${payload.search ?? ''}`,
        },
      }),
      transformResponse: (response: GetEntitlementTypessApiResponse) => {
        if (
          response.errorCode === '0' &&
          response.displayCount &&
          response.displayCount > 0
        ) {
          const uniqueValuesList: string[] = [];

          response.entitlementTypeDetails?.forEach((entitlementType) => {
            const value = entitlementType.displayName;

            if (!uniqueValuesList.includes(value)) {
              uniqueValuesList.push(value);
            }
          });

          return uniqueValuesList
            .filter((uniqueValue) => uniqueValue.trim().length > 0)
            .map((uniqueValue) => ({
              label: `${uniqueValue}`,
              value: `${uniqueValue}`,
              isChecked: false,
            }));
        }

        return [];
      },
    }),
    getMitiatingControls: builder.query<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/sodms/mitigating-control',
        params: {
          includeDetails: 'false',

          mitigatingControl: `${payload.search ?? ''}`,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetMitigatingControlsApiResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          return response.data?.map((mitigatingControl) => ({
            label: `${mitigatingControl.mitigatingControl}`,
            value: `${mitigatingControl.mitigatingControlId}`,
            isChecked: false,
          }));
        }

        return [];
      },
    }),
    getViolationsList: builder.mutation<unknown, SodViolationsListProps>({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/search',
        method: 'POST',
        data: payload,
        isECMv6: true,
      }),
    }),
    getInsightsDetails: builder.query<
      unknown,
      Omit<SodViolationsListProps, 'pagination'>
    >({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/insights/search',
        isECMv6: true,
        method: 'POST',
        params: {
          view: 'list',
        },
        data: payload,
      }),
      transformResponse: (response: GetInsightDetailsApiResponse) => {
        const finalResponse = response.data;

        return finalResponse;
      },
    }),
    getViolationsGraph: builder.mutation<
      unknown,
      Omit<SodViolationsListProps, 'pagination' | 'sort'>
    >({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/graph',
        isECMv6: true,
        method: 'POST',
        data: payload,
      }),
      transformResponse: (response: GetViolationsGraphResponse) => {
        if (response.status === 200) {
          return response.data;
        }

        return {};
      },
    }),
    getViolationsGraphCuster: builder.query<unknown, string>({
      query: (clusterId) => ({
        url: `/api/sodms/sod-violations/graph/cluster/${clusterId}`,
        isECMv6: true,
      }),
      transformResponse: (response: GetViolationsGraphClusterResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          return {
            data: response.data,
            page: response.page,
          };
        }

        return {};
      },
    }),
  }),
});

export const {
  useLazyGetTabCountQuery,
  useLazyGetColumnPickerQuery,
  useGetRisksMutation,
  useLazyGetBusinessProcessQuery,
  useLazyGetFunctionsQuery,
  useLazyGetRulesetsQuery,
  useGetApplicationEndpointsMutation,
  useGetUsersMutation,
  useGetAccountsMutation,
  useGetEntitlementsMutation,
  useLazyGetEntitlementTypesQuery,
  useLazyGetMitiatingControlsQuery,
  useGetViolationsListMutation,
  useGetInsightsDetailsQuery,
  useLazyGetInsightsDetailsQuery,
  useGetViolationsGraphMutation,
  useLazyGetViolationsGraphCusterQuery,
} = violationsTabApiEndpoints;
